import { useEffect, useRef, useState } from "react";
import { api, type AccountChangeRecord } from "./api";
import { historyDateRange } from "./history-date-range";

export default function AccountHistory() {
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const range = historyDateRange(fromDate, toDate);
  const [rows, setRows] = useState<AccountChangeRecord[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [more, setMore] = useState(false);
  const [message, setMessage] = useState("");
  const [revision, setRevision] = useState(0);
  const generation = useRef(0);

  useEffect(() => {
    const current = ++generation.current;
    setRows([]); setSelected(new Set()); setBusy(true); setMore(false);
    if (range.error) { setBusy(false); return; }
    const timer = window.setTimeout(() => {
      void api.accountChanges(search, 0, range).then((next) => {
        if (generation.current !== current) return;
        setRows(next); setMore(next.length === 250);
      }).catch((error) => {
        if (generation.current === current) setMessage(error instanceof Error ? error.message : "Could not search the account history");
      }).finally(() => { if (generation.current === current) setBusy(false); });
    }, 200);
    return () => { window.clearTimeout(timer); generation.current++; };
  }, [search, fromDate, toDate, revision]);

  async function loadMore() {
    const current = generation.current;
    setBusy(true);
    try {
      const next = await api.accountChanges(search, rows.length, range);
      if (current !== generation.current) return;
      setRows((previous) => [...new Map([...previous, ...next].map((row) => [row.id, row])).values()]);
      setMore(next.length === 250);
    } catch (error) { if (current === generation.current) setMessage(error instanceof Error ? error.message : "Could not load more history"); }
    finally { if (current === generation.current) setBusy(false); }
  }

  function changeDates(from: string, to: string) {
    generation.current++; setFromDate(from); setToDate(to); setRows([]); setSelected(new Set()); setBusy(true); setMore(false); setMessage("");
  }

  async function remove(ids: string[]) {
    if (!ids.length || !window.confirm(`Permanently delete ${ids.length} selected account-history record(s)? This cannot be undone. Accounts, project records and media will not be deleted. A new receipt will record who deleted the history and how many entries were removed.`)) return;
    setDeleting(true); setMessage("");
    try {
      const result = await api.deleteAccountChanges(ids);
      setMessage(`${result.deleted} history record(s) permanently deleted. A deletion receipt was recorded.`);
      setSelected(new Set()); setRevision((value) => value + 1);
    } catch (error) { setMessage(error instanceof Error ? error.message : "Could not delete the selected history"); }
    finally { setDeleting(false); }
  }

  return <div className="account-history-controls">
    <label>Search all recorded history<input disabled={deleting} maxLength={200} onChange={(event) => { generation.current++; setSearch(event.target.value); setSelected(new Set()); setRows([]); setBusy(true); setMessage(""); }} placeholder="Name, email, action, actor, date or changed information" type="search" value={search} /></label>
    <div className="history-date-filters">
      <label>From date<input aria-describedby="history-date-help" aria-invalid={Boolean(range.error)} disabled={deleting} type="date" value={fromDate} onChange={(event) => changeDates(event.target.value, toDate)} /></label>
      <label>To date<input aria-describedby="history-date-help" aria-invalid={Boolean(range.error)} disabled={deleting} type="date" value={toDate} onChange={(event) => changeDates(fromDate, event.target.value)} /></label>
      <button disabled={deleting || (!fromDate && !toDate)} onClick={() => changeDates("", "")} type="button">Clear dates</button>
    </div>
    <small id="history-date-help">Type a date or choose it from the calendar. Both dates are included, using your device's local time.</small>
    {range.error && <p role="alert">{range.error}</p>}
    <div className="history-selection-toolbar">
      <label><input checked={rows.length > 0 && rows.every((row) => selected.has(row.id))} disabled={busy || deleting || !rows.length || rows.length > 1000} onChange={(event) => setSelected(event.target.checked ? new Set(rows.map((row) => row.id)) : new Set())} type="checkbox" /> Select all loaded matches ({rows.length})</label>
      <button className="permanent-delete" disabled={busy || deleting || !selected.size} onClick={() => void remove([...selected])} type="button">Delete selected ({selected.size})</button>
    </div>
    <small>Search covers the complete history. Selection includes only loaded matches and clears when the search or dates change. Delete up to 1,000 records at a time.</small>
    {message && <p role="status">{message}</p>}
    <div className="account-change-list selectable-history">{rows.map((change) => <div key={change.id}>
      <label><input aria-label={`Select ${change.kind} for ${change.userName}`} checked={selected.has(change.id)} disabled={busy || deleting || (!selected.has(change.id) && selected.size >= 1000)} onChange={(event) => setSelected((previous) => { const next = new Set(previous); if (event.target.checked) next.add(change.id); else next.delete(change.id); return next; })} type="checkbox" /><strong>{change.userName || "Deleted profile"}</strong></label>
      <div><small>{change.userEmail} · {change.kind} · {new Date(change.occurredAt).toLocaleString("en-GB")}</small><small>Action by: {change.actorName || change.actorId || "Unknown historical actor"}</small>
        <p><b>Before:</b> {values(change.beforeValues)}<br /><b>After:</b> {values(change.afterValues)}</p>
      </div>
      <button className="permanent-delete" disabled={busy || deleting} onClick={() => void remove([change.id])} type="button">Delete entry</button>
    </div>)}</div>
    {busy && <p role="status">Loading history…</p>}
    {!busy && !range.error && !rows.length && <p>No history matches this search.</p>}
    {more && <button disabled={busy || deleting} onClick={() => void loadMore()} type="button">Load more matches</button>}
  </div>;
}

function values(input: Record<string, unknown>) {
  return Object.entries(input).map(([key, value]) => `${key}: ${value && typeof value === "object" ? JSON.stringify(value) : String(value ?? "—")}`).join(", ") || "—";
}
